import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Star, Users, Clock } from "lucide-react";

const FeaturedGamesSection = ({ games }) => {
  const cardGames = games.filter(g => g.category === "card");
  const boardGames = games.filter(g => g.category === "board");

  return (
    <div data-testid="featured-games-section">
      {/* Section Header */}
      <div className="mb-12">
        <h2 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
          GAMES WE LOVE
        </h2>
        <p className="mt-4 text-[#A1A1AA] font-ubuntu text-lg max-w-3xl">
          Board2Boston™ specializes in connecting players of classic card games and board games. 
          While Spades and Whist are our most popular offerings, we support a diverse range of games 
          that bring people together.
        </p>
      </div>

      {/* Premier Card Games */}
      <div className="mb-16">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-8">PREMIER CARD GAMES</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cardGames.map((game) => (
            <Card 
              key={game.id}
              className="bg-[#0A0A0A] border border-white/5 hover:border-[#7a6200]/30 transition-colors overflow-hidden group"
              data-testid={`game-card-${game.id}`}
            >
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="relative w-full md:w-48 h-48 md:h-auto overflow-hidden flex-shrink-0">
                    <img 
                      src={game.image} 
                      alt={game.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A] hidden md:block" />
                  </div>

                  {/* Content */}
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h4 className="font-bebas text-3xl tracking-wider text-white group-hover:text-[#7a6200] transition-colors">
                        {game.name}
                      </h4>
                      {game.popularity === "high" && (
                        <Badge className="bg-[#7a6200]/20 text-[#C6A300] border-[#7a6200]">
                          <Star className="w-3 h-3 mr-1" /> Popular
                        </Badge>
                      )}
                    </div>
                    <p className="text-[#A1A1AA] font-ubuntu mb-4">{game.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {game.skill_levels.map((level) => (
                        <span 
                          key={level}
                          className="px-2 py-1 text-xs font-ubuntu bg-white/5 text-[#A1A1AA] rounded capitalize"
                        >
                          {level}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Spades Highlight */}
        <div className="mt-8 p-6 bg-[#7a6200]/10 rounded-lg border border-[#7a6200]/30">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-[#7a6200]" />
            <h4 className="font-bebas text-2xl tracking-wider text-white">SPADES - FEATURED GAME</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#7a6200]" />
              <div>
                <p className="text-white font-ubuntu font-medium">Daily Games</p>
                <p className="text-[#52525B] text-sm">Available every day</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-[#7a6200]" />
              <div>
                <p className="text-white font-ubuntu font-medium">Tournament Circuit</p>
                <p className="text-[#52525B] text-sm">Monthly championships</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#7a6200]" />
              <div>
                <p className="text-white font-ubuntu font-medium">Partner Matching</p>
                <p className="text-[#52525B] text-sm">Find regular partners</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-[#7a6200]" />
              <div>
                <p className="text-white font-ubuntu font-medium">Skill Events</p>
                <p className="text-[#52525B] text-sm">All levels welcome</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Classic Board Games */}
      <div className="mb-16">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-8">CLASSIC BOARD GAMES</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {boardGames.map((game) => (
            <Card 
              key={game.id}
              className="bg-[#0A0A0A] border border-white/5 hover:border-[#7a6200]/30 transition-colors overflow-hidden group"
              data-testid={`game-card-${game.id}`}
            >
              <CardContent className="p-0">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={game.image} 
                    alt={game.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h4 className="font-bebas text-2xl tracking-wider text-white group-hover:text-[#7a6200] transition-colors">
                    {game.name}
                  </h4>
                  <p className="text-[#52525B] font-ubuntu text-sm mt-2">{game.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {game.skill_levels.map((level) => (
                      <span 
                        key={level}
                        className="px-2 py-1 text-xs font-ubuntu bg-white/5 text-[#A1A1AA] rounded capitalize"
                      >
                        {level}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Game Rules Info */}
      <div className="p-8 bg-[#0A0A0A] rounded-lg border border-white/5">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-6">
          GAME RULES AND RESOURCES
        </h3>
        <p className="text-[#A1A1AA] font-ubuntu mb-6">
          We provide comprehensive resources for every game on our platform.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-white font-ubuntu font-medium">Official Rule Sets</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Standard rules for all games</p>
          </div>
          <div>
            <p className="text-white font-ubuntu font-medium">Video Tutorials</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Learn through visual instruction</p>
          </div>
          <div>
            <p className="text-white font-ubuntu font-medium">Strategy Guides</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Improve with expert tips</p>
          </div>
          <div>
            <p className="text-white font-ubuntu font-medium">Variant Rules</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Explore different ways to play</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedGamesSection;
